import { useMemo } from 'react'
import { DropdownV5, TextV2 as Text } from '@pinchenterprisesnpm/friday-ui'
import { RosterTag } from '@src/features/shift-post-v2/steps/acceptance-criteria-step/components/who-can-work-section/components/roster-tag/roster-tag'
import { useSelectedOption } from '@src/features/shift-post-v2/steps/acceptance-criteria-step/components/who-can-work-section/components/radio-group/hooks/useSelectedOption'
import { RADIO_OPTIONS } from '@src/features/shift-post-v2/steps/acceptance-criteria-step/components/who-can-work-section/components/radio-group/radio-group.utils'
import { connect } from 'formik'
import {
  IAcceptanceCriteriaFormValues,
  ACCEPTANCE_CRITERIA_OPTION,
} from '@src/features/shift-post-v2/steps/acceptance-criteria-step/acceptance-criteria-step.types'
import { FormikProps } from 'formik'
import { AutoAcceptTypeSelector } from '@src/ui/components/common/AutoAcceptTypeSelector'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import { useSelector } from 'react-redux'
import { getSelectedAcceptanceCriteriaOption } from '@src/features/shift-post-v2/utils'
import { hasBusinessAAbyOnboardedSettingEnabled } from '@src/helpers/businessEntityHelpers'
import AccessControl from '@src/ui/components/common/AccessControl'
import ClaimCheckbox from '@src/ui/components/Admin/ClaimCheckbox'
import { Box } from '@pinchenterprisesnpm/friday-ui'
import { BoldSpan } from '@src/ui/components/common/style'
import {
  RadioButtonContainer,
  CardRadioLabel,
  CardRadioContent,
  CardRadioInput,
  CardRadioWrapper,
  DropdownWrapper,
  TitleWithTagRow,
  NewTag,
  TagsContainer,
} from './radio-group.style'
import { useRosterSelection } from './hooks/useRosterSelection'

interface IRadioGroupProps {
  formik?: FormikProps<IAcceptanceCriteriaFormValues>
}

export const RadioGroup = connect<IRadioGroupProps>(({ formik }) => {
  const { errors, values, setFieldValue } = formik

  const businessEntity = useSelector(selectBusinessEntity)
  const autoAcceptMandatory = businessEntity?.auto_accept
  const hasAutoAcceptNowButShiftDidNot = businessEntity?.auto_accept && !values.auto_accept

  const isRostersEnabled = businessEntity?.business_setting?.rosters_enabled

  const selectedAcceptanceCriteriaOption = useMemo(
    () =>
      getSelectedAcceptanceCriteriaOption(
        values.auto_accept_by_rosters,
        values.auto_accept,
        hasAutoAcceptNowButShiftDidNot ?? false
      ),
    [values.auto_accept_by_rosters, values.auto_accept, hasAutoAcceptNowButShiftDidNot]
  )

  const { selectedRosters, availableRosterOptions, handleRemoveTag, handleAddDropdownOption } =
    useRosterSelection(formik)

  const { handleSelectedOptionChange } = useSelectedOption(formik)

  const rosterPickerErrorMessage = useMemo(() => {
    if (errors.auto_accept_roster_ids && typeof errors.auto_accept_roster_ids === 'string') {
      return errors.auto_accept_roster_ids
    }

    return Array.isArray(errors.auto_accept_roster_ids)
      ? errors.auto_accept_roster_ids.join(', ')
      : ''
  }, [errors.auto_accept_roster_ids])

  // If rosters are not enabled, we don't need to show the roster option
  // If auto accept is mandatory, we don't need to show the auto accept off option
  const filteredRadioOptions = useMemo(() => {
    return RADIO_OPTIONS.filter(
      (option) =>
        (!autoAcceptMandatory || option.value !== ACCEPTANCE_CRITERIA_OPTION.PERSONALLY_APPROVED) &&
        (isRostersEnabled || option.value !== ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS)
    )
  }, [autoAcceptMandatory, isRostersEnabled])

  const handleAutoAcceptTypeChange = (value: boolean) => {
    setFieldValue('switch_to_auto_accept_after_invite_terminated', value)
  }

  return (
    <RadioButtonContainer>
      {filteredRadioOptions.map((option) => (
        <CardRadioWrapper
          onClick={(e) => {
            e.stopPropagation()
            handleSelectedOptionChange(option.value)
          }}
          key={option.value}
          selected={selectedAcceptanceCriteriaOption === option.value}
        >
          <CardRadioLabel>
            <CardRadioInput
              type='radio'
              name='who-can-work'
              value={option.value}
              checked={selectedAcceptanceCriteriaOption === option.value}
            />
            <CardRadioContent>
              <TitleWithTagRow>
                <Text variant='h6' mr={5}>
                  {option.title}
                </Text>
                {option.value === ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS && (
                  <NewTag type='plum_500' size='small'>
                    NEW
                  </NewTag>
                )}
              </TitleWithTagRow>
              <Text variant='small' color='gray'>
                {option.description}
                {option.value === ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS && (
                  <BoldSpan>&nbsp;The selected rosters will be automatically invited.</BoldSpan>
                )}
              </Text>
            </CardRadioContent>
          </CardRadioLabel>

          {/* Verified Upshifters from your Rosters */}
          {option.value === ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS &&
            selectedAcceptanceCriteriaOption ===
              ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS && (
              <>
                <DropdownWrapper>
                  <DropdownV5
                    key={selectedRosters.length}
                    value={null}
                    options={availableRosterOptions}
                    onChange={handleAddDropdownOption}
                    isMulti={false}
                    size='large'
                    type='primary'
                    rounded={false}
                    isOutlined
                    placeholder={
                      availableRosterOptions.length === 0
                        ? 'All options selected'
                        : 'Select rosters'
                    }
                    isDisabled={availableRosterOptions.length === 0}
                    errorMessage={rosterPickerErrorMessage}
                  />
                </DropdownWrapper>

                {/* Roster Tags */}
                <TagsContainer>
                  {selectedRosters.length > 0 ? (
                    <>
                      {/* Selected Roster Tags */}
                      {selectedRosters.map((item) => (
                        <RosterTag
                          key={item.value}
                          value={Number(item.value)}
                          label={item.label}
                          handleRemoveTag={handleRemoveTag}
                        />
                      ))}
                    </>
                  ) : null}
                </TagsContainer>

                {/* Auto Accept Type Selector */}
                {values.auto_accept_rosters.length > 0 && (
                  <AutoAcceptTypeSelector
                    value={values.switch_to_auto_accept_after_invite_terminated}
                    onValueChange={handleAutoAcceptTypeChange}
                    containerProps={{ mt: 'sm' }}
                  />
                )}
                <AccessControl
                  accessCheck={() => hasBusinessAAbyOnboardedSettingEnabled(businessEntity)}
                >
                  <Box mb='sm'>
                    <ClaimCheckbox
                      label='Only allow those with Onboarded Status'
                      name='auto_accept_by_onboarded'
                      handleChange={setFieldValue}
                      value={!!values.auto_accept_by_onboarded}
                    />
                  </Box>
                </AccessControl>
              </>
            )}

          {/* Auto Accept Configuration for Verified Upshifters */}
          {option.value === ACCEPTANCE_CRITERIA_OPTION.VERIFIED &&
            selectedAcceptanceCriteriaOption === ACCEPTANCE_CRITERIA_OPTION.VERIFIED && (
              <AccessControl
                accessCheck={() => hasBusinessAAbyOnboardedSettingEnabled(businessEntity)}
              >
                <Box mb='sm'>
                  <ClaimCheckbox
                    label='Only allow those with Onboarded Status'
                    name='auto_accept_by_onboarded'
                    handleChange={setFieldValue}
                    value={!!values.auto_accept_by_onboarded}
                  />
                </Box>
              </AccessControl>
            )}
        </CardRadioWrapper>
      ))}
    </RadioButtonContainer>
  )
})

export default RadioGroup
